import { useEffect, useState } from "react";
import {
  deleteIcon,
  downloadIcon,
  filterIcon,
  receiptLongIcon,
} from "../../assets/icons/SvgIconList";
import ActionIcon from "../../components/purchase/ActionIcon";
import UseOrderDocumentDownloadHook from "../../hooks/UseOrderDocumentDownloadHook";
import StatusChip from "../../components/purchase/StatusChip";
import { ICCheckbox, ICDropDown } from "../../core-component/form";
import {
  AdvanceSearch,
  ICButton,
  ICConfirmationModal,
  ICTable,
} from "../../core-component/ui";
import { IOption, IPagination } from "../../data/AppInterface";
import UseEventCallbackHook from "../../hooks/UseEventCallbackHook";
import UsePaginationHook from "../../hooks/UsePaginationHook";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { IFirmPurchasedSubscriptionFilter } from "../../services/firm-purchased-subscription/FirmPurchasedSubscriptionService";
import { ORDER_BY } from "../../utils/AppConstants";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import {
  escapeHtml,
  prepareMessageFromParams,
} from "../../utils/AppFunctions";
import {
  CHIP_CLASS,
  getSourceOptions,
  SUBSCRIPTION_SOURCE_CHIP_CLASS,
  SUBSCRIPTION_SOURCE_LABEL_KEY,
} from "../../utils/PurchaseConstants";
import {
  DOCUMENT_TYPE_ENUM,
  SUBSCRIPTION_END_REASON_ENUM,
  SUBSCRIPTION_SOURCE_ENUM,
} from "../../utils/PurchaseEnumerations";
import { formatPurchaseDate } from "../../utils/PurchaseFormat";
import FirmSubscriptionAssignmentForm from "./FirmSubscriptionAssignmentForm";
import {
  cancelFirmSubscription,
  FIRM_PURCHASED_SUBSCRIPTION_FILTER_INITIAL_VALUE,
  getAllFirmPurchasedSubscription,
  getFirmNameDdl,
  getFirmPurchasedSubscriptionFields,
  getSubscriptionRowClass,
  IFirmPurchasedSubscription,
  isRunningSubscription,
} from "./FirmPurchasedSubscriptionViewController";

let timer: ReturnType<typeof setTimeout>;

const FirmPurchasedSubscriptionView = () => {
  const { t } = UseTranslationHook();
  const { download, isBusy } = UseOrderDocumentDownloadHook();
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const { pagination, setChangePagination } = UsePaginationHook({
    pagination: { perPageRows: 10, currentPage: 1, sortBy: "subscription_date", orderBy: ORDER_BY.desc },
  });
  const [list, setList] = useState<IFirmPurchasedSubscription[]>([]);
  const [firmOptions, setFirmOptions] = useState<IOption<string>[]>([]);
  const [showAssignmentForm, setShowAssignmentForm] = useState(false);
  const [subscriptionToCancel, setSubscriptionToCancel] = useState<IFirmPurchasedSubscription>();
  const [filter, setFilter] = useState<IFirmPurchasedSubscriptionFilter>(
    FIRM_PURCHASED_SUBSCRIPTION_FILTER_INITIAL_VALUE,
  );

  const fetchList = (paginationPayload: IPagination) => {
    setIsLoading(true);
    if (timer) clearTimeout(timer);
    timer = setTimeout(async () => {
      await getAllFirmPurchasedSubscription(setList, paginationPayload, setChangePagination, search, filter);
      setIsLoading(false);
    }, 500);
  };

  const fetchFirstPage = UseEventCallbackHook(() => fetchList({ ...pagination, currentPage: 1 }));

  useEffect(() => {
    fetchFirstPage();
  }, [search, filter, fetchFirstPage]);

  useEffect(() => {
    getFirmNameDdl(setFirmOptions);
  }, []);

  const handleCancel = async (confirmed: boolean) => {
    const subscription = subscriptionToCancel;
    setSubscriptionToCancel(undefined);
    if (confirmed && subscription && (await cancelFirmSubscription(subscription.id))) {
      fetchList(pagination);
    }
  };

  const handleCloseAssignmentForm = (status: boolean) => {
    setShowAssignmentForm(false);
    if (status) fetchList(pagination);
  };

  const sourceCell = (row: IFirmPurchasedSubscription) => (
    <StatusChip
      label={t(SUBSCRIPTION_SOURCE_LABEL_KEY[row.source])}
      className={SUBSCRIPTION_SOURCE_CHIP_CLASS[row.source]}
    />
  );

  const endDateCell = (row: IFirmPurchasedSubscription) => (
    <div className="flex flex-col items-start gap-1">
      <span>{formatPurchaseDate(row.end_date)}</span>
      {row.end_reason === SUBSCRIPTION_END_REASON_ENUM.Replaced && (
        <StatusChip label={t("replaced")} className={CHIP_CLASS.grey} />
      )}
      {row.end_reason === SUBSCRIPTION_END_REASON_ENUM.Cancelled && (
        <StatusChip label={t("cancelled")} className={CHIP_CLASS.danger} />
      )}
    </div>
  );

  const customActionView = (item: IFirmPurchasedSubscription) => {
    const orderId = item.order_id;
    const isBought = item.source === SUBSCRIPTION_SOURCE_ENUM.Purchase && !!orderId;
    return (
      <div className="flex items-center justify-center w-full gap-2">
        {isBought &&
          orderId && (
            <ActionIcon
              icon={downloadIcon}
              title={t("download_invoice")}
              onClick={() => download(orderId, DOCUMENT_TYPE_ENUM.Invoice)}
              busy={isBusy(orderId, DOCUMENT_TYPE_ENUM.Invoice)}
              className="text-accent"
            />
          )}
        {isBought &&
          orderId && (
            <ActionIcon
              icon={receiptLongIcon}
              title={t("download_receipt")}
              onClick={() => download(orderId, DOCUMENT_TYPE_ENUM.Receipt)}
              busy={isBusy(orderId, DOCUMENT_TYPE_ENUM.Receipt)}
              className="text-accent"
            />
          )}
        {isRunningSubscription(item) && (
            <ActionIcon
              icon={deleteIcon}
              title={t("cancel_subscription_tooltip_icon")}
              onClick={() =>
                setSubscriptionToCancel(item)}
            />
          )}
      </div>
    );
  };

  const filterView = (
    <div className="w-full grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 items-center">
      <ICDropDown
        placeholder={t("firm_name")}
        selected={filter.firmId ?? undefined}
        options={firmOptions}
        searchable
        onSelect={(option) => setFilter((previous) => ({ ...previous, firmId: option.data_value }))}
      />
      <ICDropDown
        placeholder={t("source")}
        selected={filter.source ?? undefined}
        options={getSourceOptions(t)}
        onSelect={(option) => setFilter((previous) => ({ ...previous, source: option.data_value }))}
      />
      <ICCheckbox
        label={t("show_historical_records")}
        checkboxClassName="ml-4"
        checked={filter.showHistoricalRecords === BIT_VALUE.One}
        onChange={(checked) =>
          setFilter((previous) => ({
            ...previous,
            showHistoricalRecords: checked ? BIT_VALUE.One : BIT_VALUE.Zero,
          }))
        }
      />
      <ICButton
        type="button"
        onClick={() => setFilter(FIRM_PURCHASED_SUBSCRIPTION_FILTER_INITIAL_VALUE)}
        className="flex !w-fit"
      >
        <div className="size-5 grid place-self-center">{filterIcon}</div>
        &nbsp;{t("clear")}
      </ICButton>
    </div>
  );

  return (
    <div className="w-full overflow-scroll scrollbar-remove p-4">
      <AdvanceSearch
        handleOnClickAdd={() => setShowAssignmentForm(true)}
        handleSearchChange={setSearch}
        searchText={search}
        filterNode={filterView}
        refreshTable={() => fetchList(pagination)}
      />
      <ICTable<IFirmPurchasedSubscription>
        isLoading={isLoading}
        totalFlex="grid grid-cols-20"
        fields={getFirmPurchasedSubscriptionFields(
          { action: customActionView, source: sourceCell, endDate: endDateCell },
          t,
        )}
        data={list}
        pagination={pagination}
        rowClassName={getSubscriptionRowClass}
        handleChangePerPageRows={(perPageRows) => fetchList({ ...pagination, perPageRows, currentPage: 1 })}
        handleChangePage={(page) => fetchList({ ...pagination, currentPage: page })}
        handleSort={(field) =>
          fetchList({
            ...pagination,
            sortBy: field.key,
            orderBy: pagination.orderBy === ORDER_BY.desc ? ORDER_BY.asc : ORDER_BY.desc,
          })
        }
      />
      {showAssignmentForm && <FirmSubscriptionAssignmentForm handleClose={handleCloseAssignmentForm} />}
      {subscriptionToCancel && (
        <ICConfirmationModal
          title={t("cancel_subscription")}
          message={prepareMessageFromParams(t("cancel_subscription_message"), [
            ["packageName", escapeHtml(subscriptionToCancel.subscription_package_name)],
            ["firmName", escapeHtml(subscriptionToCancel.firm_name)],
          ])}
          onClose={handleCancel}
        />
      )}
    </div>
  );
};

export default FirmPurchasedSubscriptionView;
